import { backgroundRenderer } from '../../../core/services/BackgroundRenderer.js';
import { storageManager } from '../../../core/storage/index.js';
import { HangoutState } from '../state/HangoutState.js';
import { $, css } from '../../../core/utils/dom.js';

export class LocationTransitionManager {
    constructor() {
        this.isTransitioning = false;
        this.isFirstLoad = false;
        this.currentLocationId = null;
        this.isEnabled = true;
        this.isLocationTitleEnabled = true;
        this.transitionDuration = 2000;
        this.characterTransitionDuration = 800;
        this.backgroundTransitionDuration = 1000;
        this.blackoutDuration = 600;
        this.pendingOutfitChanges = new Map();
        this._titleOverlay = null;
        this._titleNameEl = null;
        this._titleSeq = 0;
        this._titleShowTimeout = null;
        this._titleHideTimeout = null;
        this._titleFadeTimeout = null;
        this._titleWaitResolve = null;
    }

    async initialize() {
        try {
            const { getBackgroundService } = await import('../../../core/services/BackgroundService.js');
            const backgroundService = getBackgroundService();
            const currentBackground = await backgroundService.getCurrentBackground();
            
            this.currentLocationId = currentBackground.locationId || '31';
        } catch (error) {
            this.currentLocationId = '31';
        }
    }

    setNotFirstLoad() {
        this.isFirstLoad = false;
    }

    shouldTransition(newLocationId) {
        return !this.isTransitioning && this.isEnabled && this.currentLocationId !== newLocationId;
    }

    async transitionForInitialScene(newLocationId, timeOfDay) {
        this.showLocationTitle(newLocationId, timeOfDay);
        this.currentLocationId = newLocationId;
        return true;
    }

    async transitionToLocation(newLocationId, newTimeOfDay = 'night') {
        if (!this.shouldTransition(newLocationId)) {
            this.currentLocationId = newLocationId;
            return false;
        }

        this.isTransitioning = true;

        try {
            // Immediately update background and sprites (like timeset)
            await this.updateBackgroundAndSprites(newLocationId, newTimeOfDay);
            
            // Trigger location title fade effect in parallel (do not block other UI updates)
            this.showLocationTitle(newLocationId, newTimeOfDay).catch(() => {});
            
            this.currentLocationId = newLocationId;
            return true;
        } catch (error) {
            return false;
        } finally {
            this.isTransitioning = false;
        }
    }

    async transitionForLocationChange(locationId, timeOfDay = 'night') {
        if (this.isTransitioning) return false;
        this.isTransitioning = true;

        try {
            await this.storeCharacterBubbleState();
            await this.slideOutCharacters();
            await this.fadeToBlackout();
            await this.updateBackgroundAndSprites(locationId, timeOfDay);
            await this.fadeInBackground();
            await this.showLocationTitleOverlay(locationId, timeOfDay);
            await this.slideInCharacters();

            this.currentLocationId = locationId;
            return true;
        } catch (error) {
            return false;
        } finally {
            this.isTransitioning = false;
        }
    }

    async transitionForTimeChangeSameLocation() {
        if (this.isTransitioning) return false;
        this.isTransitioning = true;

        try {
            const config = await storageManager.getItem('currentHangoutConfig') || {};
            const currentLocationId = config.loc_id || this.currentLocationId;
            const currentTimeOfDay = config.timeOfDay || 'night';

            await this.storeCharacterBubbleState();
            await this.slideOutCharacters();
            await this.fadeToBlackout();
            await this.updateCharacterSpritesOnly();
            await this.fadeInBackground();
            await this.showLocationTitleOverlay(currentLocationId, currentTimeOfDay);
            await this.slideInCharacters();

            return true;
        } catch (error) {
            return false;
        } finally {
            this.isTransitioning = false;
        }
    }

    async storeCharacterBubbleState() {
        const bubbleContainer = document.getElementById('character-bubbles-container');
        if (bubbleContainer) {
            this._originalBubbleDisplay = bubbleContainer.style.display || '';
            this._originalBubbleOpacity = bubbleContainer.style.opacity || 1;
        }
    }

    async showCharacterBubbles() {
        delete this._originalBubbleDisplay;
        delete this._originalBubbleOpacity;
    }

    async slideOutCharacters() {
        const characterContainers = this.getAllCharacterContainers();
        if (characterContainers.length === 0) return;

        characterContainers.forEach(container => {
            container._originalTransform = container.style.transform;
        });

        const bubbleContainer = document.getElementById('character-bubbles-container');
        let characterBubbles = [];
        if (bubbleContainer) {
            characterBubbles = Array.from(bubbleContainer.querySelectorAll('.character-message-bubble'));
            characterBubbles.forEach(bubble => {
                bubble._originalTransform = bubble.style.transform;
                bubble._originalTransition = bubble.style.transition;
            });
        }

        const slidePromises = characterContainers.map((container, index) => {
            return new Promise(resolve => {
                const delay = index * 100;

                setTimeout(() => {
                    container.style.transition = `transform ${this.characterTransitionDuration}ms cubic-bezier(0.4, 0, 0.6, 1), opacity ${this.characterTransitionDuration}ms ease-in`;
                    container.style.transform = 'translateX(-120vw) scale(0.6)';
                    container.style.opacity = '0';

                    characterBubbles.forEach(bubble => {
                        bubble.style.transition = `transform ${this.characterTransitionDuration}ms cubic-bezier(0.4, 0, 0.6, 1), opacity ${this.characterTransitionDuration}ms ease-in`;
                        bubble.style.transform = 'translateX(-120vw) scale(0.8)';
                        bubble.style.opacity = '0.3';
                    });
                }, delay);

                setTimeout(resolve, this.characterTransitionDuration + delay);
            });
        });

        await Promise.all(slidePromises);
    }

    async fadeToBlackout() {
        const blackoutOverlay = document.createElement('div');
        blackoutOverlay.className = 'location-transition-blackout';
        blackoutOverlay.style.cssText = `
            position: fixed;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background: #000000;
            z-index: 10000;
            opacity: 0;
            transition: opacity ${this.backgroundTransitionDuration}ms ease-in-out;
            pointer-events: none;
        `;

        document.body.appendChild(blackoutOverlay);

        const hangoutContainer = document.getElementById('hangout-container');
        if (hangoutContainer) {
            hangoutContainer.style.transition = `opacity ${this.backgroundTransitionDuration}ms ease-in-out`;
            hangoutContainer.style.opacity = '0';
        }

        setTimeout(() => {
            blackoutOverlay.style.opacity = '1.0';
        }, 50);

        await new Promise(resolve => setTimeout(resolve, this.backgroundTransitionDuration + this.blackoutDuration));
        this._blackoutOverlay = blackoutOverlay;
    }

    async updateBackgroundAndSprites(newLocationId, newTimeOfDay) {
        try {
            const { getBackgroundService } = await import('../../../core/services/BackgroundService.js');
            const backgroundService = getBackgroundService();
            const newBackgroundUrl = await backgroundService.constructBackgroundUrl(newLocationId, newTimeOfDay);

            await backgroundRenderer.applyBackgroundImage(newBackgroundUrl);
            this._newBackgroundUrl = newBackgroundUrl;

            if (this.hasPendingOutfitChanges()) {
                await this.applyPendingOutfitChanges();
            }

            const { characterManager } = await import('./character-manager.js');
            await characterManager.refreshAllCharacterSprites();
        } catch (error) {
            // Silent error handling
        }
    }

    async updateCharacterSpritesOnly() {
        try {
            const { characterManager } = await import('./character-manager.js');
            await characterManager.refreshAllCharacterSprites();
        } catch (error) {
            // Silent error handling
        }
    }

    async updateBackgroundOnly(newLocationId, newTimeOfDay) {
        try {
            const { getBackgroundService } = await import('../../../core/services/BackgroundService.js');
            const backgroundService = getBackgroundService();
            const newBackgroundUrl = await backgroundService.constructBackgroundUrl(newLocationId, newTimeOfDay);

            await backgroundRenderer.applyBackgroundImage(newBackgroundUrl);
            this._newBackgroundUrl = newBackgroundUrl;
        } catch (error) {
            // Silent error handling
        }
    }

    async fadeInBackground() {
        const hangoutContainer = document.getElementById('hangout-container');
        if (!hangoutContainer) return;

        return new Promise(resolve => {
            if (this._blackoutOverlay) {
                this._blackoutOverlay.style.transition = `opacity ${this.backgroundTransitionDuration}ms ease-out`;
                this._blackoutOverlay.style.opacity = '0';

                setTimeout(() => {
                    if (this._blackoutOverlay && this._blackoutOverlay.parentNode) {
                        this._blackoutOverlay.remove();
                    }
                    this._blackoutOverlay = null;
                }, this.backgroundTransitionDuration);
            }

            hangoutContainer.style.transition = `opacity ${this.backgroundTransitionDuration}ms ease-out`;
            hangoutContainer.style.opacity = '1';

            setTimeout(() => {
                if (this._newBackgroundUrl) {
                    delete this._newBackgroundUrl;
                }
                resolve();
            }, this.backgroundTransitionDuration);
        });
    }

    ensureTitleOverlay() {
        if (this._titleOverlay?.isConnected) {
            return this._titleOverlay;
        }

        const titleOverlay = document.createElement('div');
        titleOverlay.className = 'location-title-overlay';
        titleOverlay.setAttribute('aria-hidden', 'true');

        const locationElement = document.createElement('h1');
        locationElement.className = 'location-title-overlay__location';

        titleOverlay.appendChild(locationElement);
        document.body.appendChild(titleOverlay);

        this._titleOverlay = titleOverlay;
        this._titleNameEl = locationElement;
        return titleOverlay;
    }

    clearTitleTimers() {
        if (this._titleShowTimeout) {
            clearTimeout(this._titleShowTimeout);
            this._titleShowTimeout = null;
        }
        if (this._titleHideTimeout) {
            clearTimeout(this._titleHideTimeout);
            this._titleHideTimeout = null;
        }
        if (this._titleFadeTimeout) {
            clearTimeout(this._titleFadeTimeout);
            this._titleFadeTimeout = null;
        }
        const resolveWait = this._titleWaitResolve;
        this._titleWaitResolve = null;
        if (resolveWait) resolveWait();
    }

    async resolveLocationIdForTitle(locationId) {
        if (locationId && locationId !== 'null' && locationId !== 'undefined') {
            return locationId;
        }

        try {
            const { getBackgroundService } = await import('../../../core/services/BackgroundService.js');
            const backgroundService = getBackgroundService();
            const currentBackground = await backgroundService.getCurrentBackground();
            locationId = currentBackground.locationId;
        } catch (bgError) {
            locationId = '31';
        }

        if (!locationId || locationId === 'null' || locationId === 'undefined') {
            return '31';
        }

        return locationId;
    }

    async presentLocationTitle(locationId, { variant, holdMs, fadeMs }) {
        try {
            if (!this.isLocationTitleEnabled) return;

            this._titleSeq += 1;
            const seq = this._titleSeq;
            this.clearTitleTimers();

            locationId = await this.resolveLocationIdForTitle(locationId);
            if (seq !== this._titleSeq) return;

            const locationName = await this.getLocationName(locationId);
            if (seq !== this._titleSeq) return;

            const titleOverlay = this.ensureTitleOverlay();
            this._titleNameEl.textContent = locationName;

            titleOverlay.classList.remove('is-visible', 'location-title-overlay--corner', 'location-title-overlay--center');
            titleOverlay.classList.add(`location-title-overlay--${variant}`);
            void titleOverlay.offsetWidth;

            await new Promise((resolve) => {
                this._titleWaitResolve = resolve;
                this._titleShowTimeout = setTimeout(() => {
                    if (seq !== this._titleSeq) {
                        resolve();
                        return;
                    }
                    titleOverlay.classList.add('is-visible');

                    this._titleHideTimeout = setTimeout(() => {
                        if (seq !== this._titleSeq) {
                            resolve();
                            return;
                        }
                        titleOverlay.classList.remove('is-visible');

                        this._titleFadeTimeout = setTimeout(() => {
                            if (this._titleWaitResolve === resolve) {
                                this._titleWaitResolve = null;
                            }
                            resolve();
                        }, fadeMs);
                    }, holdMs);
                }, 200);
            });
        } catch (error) {
            // Silent error handling
        }
    }

    async showLocationTitle(locationId, timeOfDay) {
        await this.presentLocationTitle(locationId, {
            variant: 'corner',
            holdMs: 8000,
            fadeMs: 600
        });
    }

    async showLocationTitleOverlay(locationId, timeOfDay) {
        await this.presentLocationTitle(locationId, {
            variant: 'center',
            holdMs: 2000,
            fadeMs: 800
        });
    }

    async slideInCharacters() {
        const characterContainers = this.getAllCharacterContainers();
        if (characterContainers.length === 0) return;

        const bubbleContainer = document.getElementById('character-bubbles-container');
        let characterBubbles = [];
        if (bubbleContainer) {
            characterBubbles = Array.from(bubbleContainer.querySelectorAll('.character-message-bubble'));
        }

        // Instantly show characters without animation
        // Only restore containers that were previously hidden (have _originalTransform set)
        // This prevents clearing the transform of containers that weren't part of the slide-out
        characterContainers.forEach(container => {
            // Skip containers that weren't slid out (no stored state)
            // This preserves the positioning of existing characters when new ones are added
            if (!Object.prototype.hasOwnProperty.call(container, '_originalTransform')) {
                return;
            }

            container.style.transition = 'none';
            const originalTransform = container._originalTransform || '';
            const originalVisibility = container._originalVisibility || 'visible';
            const originalOpacity = container._originalOpacity || '1';

            container.style.transform = originalTransform;
            container.style.visibility = originalVisibility;
            container.style.opacity = originalOpacity;

            delete container._originalTransform;
            delete container._originalVisibility;
            delete container._originalOpacity;
        });

        characterBubbles.forEach(bubble => {
            bubble.style.transition = 'none';
            const originalBubbleTransform = bubble._originalTransform || '';
            const originalBubbleVisibility = bubble._originalVisibility || 'visible';
            const originalBubbleOpacity = bubble._originalOpacity || '1';

            bubble.style.transform = originalBubbleTransform;
            bubble.style.visibility = originalBubbleVisibility;
            bubble.style.opacity = originalBubbleOpacity;

            delete bubble._originalTransform;
            delete bubble._originalVisibility;
            delete bubble._originalOpacity;
        });
    }

    getAllCharacterContainers() {
        const containers = [];
        const multiCharContainers = document.querySelectorAll('[class*="character-image-"]');
        multiCharContainers.forEach(container => {
            if (container.style.display !== 'none') {
                containers.push(container);
            }
        });

        if (containers.length === 0) {
            const singleCharContainer = document.querySelector('.character-image:not([class*="character-image-"])');
            if (singleCharContainer && singleCharContainer.style.display !== 'none') {
                containers.push(singleCharContainer);
            }
        }
        return containers;
    }

    completeTransitionImmediately() {
        if (!this.isTransitioning) return;

        const characterContainers = this.getAllCharacterContainers();
        const hangoutContainer = document.getElementById('hangout-container');

        characterContainers.forEach(container => {
            container.style.transition = 'none';
            container.style.opacity = '1';
            // Only restore transform if container was part of slide-out
            if (Object.prototype.hasOwnProperty.call(container, '_originalTransform')) {
                container.style.transform = container._originalTransform || '';
            }
        });

        if (hangoutContainer) {
            hangoutContainer.style.transition = 'none';
            hangoutContainer.style.opacity = '1';
        }

        this.isTransitioning = false;
    }

    isCurrentlyTransitioning() {
        return this.isTransitioning;
    }

    async getLocationName(locationId) {
        const { getLocationNameById } = await import('../../../core/utils/hangoutUtils.js');
        return getLocationNameById(locationId);
    }

    formatCurrentTime() {
        const formatted = window.timeProgressionService?.getFormattedTime?.();
        if (formatted) return formatted;

        const now = new Date();
        return now.toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: false
        });
    }

    async getTimeOfDayLabel(timeOfDay) {
        const { getTimeDefinitionService } = await import('../../../core/services/TimeDefinitionService.js');
        const timeService = getTimeDefinitionService();
        return timeService.getTimeOfDayLabel(timeOfDay);
    }

    async showCurrentLocationText() {
        try {
            if (!this.isLocationTitleEnabled) return false;
            const config = await storageManager.getItem('currentHangoutConfig') || {};
            const currentLocationId = config.loc_id || this.currentLocationId;
            const currentTimeOfDay = config.timeOfDay || 'night';

            if (!currentLocationId || currentLocationId === '0') {
                return false;
            }

            await this.showLocationTitle(currentLocationId, currentTimeOfDay);
            return true;
        } catch (error) {
            return false;
        }
    }

    setPendingOutfitChange(characterId, outfitId) {
        this.pendingOutfitChanges.set(String(characterId), outfitId);
    }

    clearPendingOutfitChanges() {
        this.pendingOutfitChanges.clear();
    }

    hasPendingOutfitChanges() {
        return this.pendingOutfitChanges.size > 0;
    }

    async applyPendingOutfitChanges() {
        try {
            const currentConfig = await storageManager.getItem('currentHangoutConfig') || {};

            if (!currentConfig.allCharacters) return;

            for (const [characterId, outfitId] of this.pendingOutfitChanges) {
                const characterIndex = currentConfig.allCharacters.findIndex(
                    c => String(c.id) === String(characterId)
                );
                if (characterIndex !== -1) {
                    currentConfig.allCharacters[characterIndex].outfitId = outfitId;
                }
            }

            await storageManager.setItem('currentHangoutConfig', currentConfig);
            this.clearPendingOutfitChanges();
        } catch (error) {
            // Silent error handling
        }
    }

    async slideOutCharacterByIndex(characterIndex, removeBubbles = true) {
        const characterContainers = this.getAllCharacterContainers();
        const targetContainer = characterContainers.find(container => {
            const containerClass = container.className;
            return containerClass.includes(`character-image-${characterIndex}`) ||
                (characterIndex === 0 && containerClass.includes('character-image') && !containerClass.includes('character-image-1') && !containerClass.includes('character-image-2'));
        });

        if (!targetContainer) return;

        const characterId = targetContainer.getAttribute('data-character-id');
        let characterBubbles = [];
        if (removeBubbles && characterId) {
            const bubbleContainer = document.getElementById('character-bubbles-container');
            if (bubbleContainer) {
                characterBubbles = Array.from(bubbleContainer.querySelectorAll(`.character-message-bubble[data-character-id="${characterId}"]`));
            }
        }

        targetContainer._originalTransform = targetContainer.style.transform;
        targetContainer._originalOpacity = targetContainer.style.opacity;

        characterBubbles.forEach(bubble => {
            bubble._originalTransform = bubble.style.transform;
            bubble._originalOpacity = bubble.style.opacity;
        });

        return new Promise(resolve => {
            targetContainer.style.transition = `transform ${this.characterTransitionDuration}ms cubic-bezier(0.4, 0, 0.6, 1), opacity ${this.characterTransitionDuration}ms ease-in`;
            targetContainer.style.transform = 'translateX(-120vw) scale(0.6)';
            targetContainer.style.opacity = '0';

            characterBubbles.forEach(bubble => {
                bubble.style.transition = `transform ${this.characterTransitionDuration}ms cubic-bezier(0.4, 0, 0.6, 1), opacity ${this.characterTransitionDuration}ms ease-in`;
                bubble.style.transform = 'translateX(-120vw) scale(0.8)';
                bubble.style.opacity = '0';
            });

            setTimeout(() => {
                targetContainer.style.display = 'none';
                targetContainer.remove();

                if (removeBubbles) {
                    characterBubbles.forEach(bubble => {
                        bubble.remove();
                    });
                }
                resolve();
            }, this.characterTransitionDuration);
        });
    }
}

let transitionManagerInstance = null;

export function getLocationTransitionManager() {
    if (!transitionManagerInstance) {
        transitionManagerInstance = new LocationTransitionManager();
    }
    return transitionManagerInstance;
}

export async function applyHangoutLocationTransition({
    locationId,
    timeOfDay,
    backgroundUrl,
    isInitialGreeting = false,
    skipIfMatchesConfig = true,
    getBackgroundServiceInstance,
    getCurrentBackgroundSafely
} = {}) {
    const hangoutState = HangoutState.getInstance();
    const currentTimeOfDay = hangoutState.getCurrentTimeSlot() || timeOfDay || 'night';

    let resolvedLocationId = locationId;
    if (!resolvedLocationId || resolvedLocationId === 'null' || resolvedLocationId === 'undefined') {
        const backgroundResult = await getCurrentBackgroundSafely();
        resolvedLocationId = backgroundResult.locationId;
    }

    const shouldUseInitialTransition = isInitialGreeting || (typeof window !== 'undefined' && window.isInitialGreetingStarting) || false;

    if (skipIfMatchesConfig && !shouldUseInitialTransition) {
        const currentConfig = await storageManager.getItem('currentHangoutConfig') || {};
        const currentLocId = currentConfig.loc_id;
        if (currentLocId && String(currentLocId) === String(resolvedLocationId)) {
            return;
        }
    }

    const transitionManager = getLocationTransitionManager();
    try {
        if (shouldUseInitialTransition) {
            if (typeof window !== 'undefined') window.isInitialGreetingStarting = false;
            await transitionManager.transitionForInitialScene(String(resolvedLocationId), currentTimeOfDay);
        } else {
            await transitionManager.transitionToLocation(String(resolvedLocationId), currentTimeOfDay);
        }

        try {
            const backgroundService = await getBackgroundServiceInstance();
            await backgroundService.setBackground(resolvedLocationId, currentTimeOfDay);
        } catch (bgError) {
            console.warn('location-transition.js: Error syncing BackgroundService after transition:', bgError);
        }
    } catch (error) {
        console.error('location-transition.js: Error applying location transition:', error);
        const backgroundCanvas = $('#background-canvas');
        if (backgroundCanvas && backgroundUrl) {
            css(backgroundCanvas, {
                backgroundImage: `url(${backgroundUrl})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat'
            });
        }
    }
} 
